import moment from 'moment';

import { Button, Card, Popover, Space, Table, Tag, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ClockCircleOutlined, DeleteOutlined, DownloadOutlined, ExclamationCircleOutlined, RedoOutlined } from '@ant-design/icons';

import {
  refreshRabbitSignDocument,
  searchRabbitSignDocuments,
  sendRabbitSignDocumentReminder,
  cancelRabbitSignDocument,
  downloadExternalRabbitSignDocument,
} from '../../store/actions';

import withErrorHandler from '../../../../hoc/with-error-handler';
import axios from '../../../../shared/axios';

import { AppDispatch, RootState } from '../../../../store';

import { PagedResponse } from '../../../../shared/models/paged-response';
import { RabbitActionTypes } from '../../constants/enums/rabbit-action-types';
import { RabbitSignApiKeyType } from '../../constants/enums/rabbit-sign-api-key-type';
import { rabbitSignFolderStatuses as DocStatus } from '../../constants/folder-statuses';
import { rabbitSignSignerStatuses as SignerStatus } from '../../constants/signer-statuses';

import { RabbitSignDocument } from '../../models/rabbit-sign-document';
import { RabbitSignSigner } from '../../models/rabbit-sign-signer';
import { GetRabbitSignDocumentsQuery } from '../../models/request-models/get-rabbit-sign-documents-query';
import { RabbitSignDocumentCommandBase } from '../../models/request-models/rabbit-sign-document-command-base';
import { SizeType } from 'antd/es/config-provider/SizeContext';

import './styles.less';

export type ESignDocumentsComponentProps = {
  insuredId: string;
};

const ESignDocumentsComponent = (props: ESignDocumentsComponentProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const didMount = useRef(false);

  const defaultRequest: GetRabbitSignDocumentsQuery = {
    pageSize: 10,
    pageNumber: 1,
    sortDirection: 'desc',
    filter: {
      insuredId: props.insuredId,
    },
  };
  const [searchRequest, setSearchRequest] = useState<GetRabbitSignDocumentsQuery>(defaultRequest);

  const rabbitSignResponse: PagedResponse<RabbitSignDocument> | null = useSelector((state: RootState) => state.rabbitSign.rabbitSignDocuments);
  const loading = useSelector((state: RootState) => state.rabbitSign.rabbitSignDocumentsLoading);

  useEffect(() => {
    if (!didMount.current) {
      searchDocuments(searchRequest);
      didMount.current = true;
    }
  }, []);

  const searchDocuments = (request: GetRabbitSignDocumentsQuery) => {
    setSearchRequest(request);

    dispatch(searchRabbitSignDocuments(request));
  };

  const doRabbitSignAction = (actionType: RabbitActionTypes, record: RabbitSignDocument) => {
    const request: RabbitSignDocumentCommandBase = {
      documentId: record.id,
    };

    switch (actionType) {
      case RabbitActionTypes.Download:
        dispatch(downloadExternalRabbitSignDocument(record));
        break;
      case RabbitActionTypes.SendReminder:
        dispatch(sendRabbitSignDocumentReminder(request));
        break;
      case RabbitActionTypes.CancelDocument:
        dispatch(cancelRabbitSignDocument(request));
        break;
      case RabbitActionTypes.RefreshStatus:
        dispatch(refreshRabbitSignDocument(request));
        break;
    }
  };

  const renderTotal = (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} documents`;

  const onChangePage = (page: number, size: number) => {
    let request = searchRequest;
    request.pageSize = size;
    request.pageNumber = page;

    searchDocuments(request);
  };

  const antDefaultSize: SizeType = 'small';

  const btnConfigs = {
    variant: 'solid' as const,
    size: antDefaultSize,
  };

  const getFolderStatusTag = (status: string | null) => {
    status = status ?? DocStatus.created;
    const statusColor =
      status === DocStatus.created ? 'warning' : status === DocStatus.signed ? 'success' : status === DocStatus.canceled ? 'error' : 'default';
    return <Tag color={statusColor}>{status}</Tag>;
  };

  const getSignerStatusTag = (status: string | null) => {
    status = status ?? SignerStatus.notified;
    const statusColor = status === SignerStatus.notified ? 'warning' : 'success';
    return (
      <Tag color={statusColor} className="signer-tag">
        {status}
      </Tag>
    );
  };

  return (
    <div className="rabbit-sign-docs">
      <Card
        className="rabbit-sign-document-card"
        title={
          <div className="rabbit-sign-docs-card-header">
            <Typography.Title level={5} className="card-title">
              Rabbit Sign Documents
            </Typography.Title>
            <Button
              type="primary"
              className="refresh-documents-btn"
              title="Reload Rabbit Sign documents"
              onClick={() => searchDocuments(searchRequest)}
              loading={loading}
            >
              <RedoOutlined />
            </Button>
          </div>
        }
      >
        <Table<RabbitSignDocument>
          className="rabbit-sign-docs-table"
          rowKey="id"
          loading={loading}
          dataSource={rabbitSignResponse?.data || []}
          pagination={{
            size: antDefaultSize,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: (page, pageSize) => onChangePage(page, pageSize),
            pageSize: searchRequest?.pageSize || defaultRequest.pageSize,
            current: searchRequest?.pageNumber || defaultRequest.pageNumber,
            total: rabbitSignResponse?.totalCount || 0,
            showTotal: (total, range) => renderTotal(total, range),
          }}
          scroll={{ x: 'max-content' }}
          size={antDefaultSize}
        >
          <Table.Column
            className="rabbit-sign-doc-cell"
            title={<span>Actions</span>}
            key="actions"
            render={(_: any, record: RabbitSignDocument) => (
              <Space key={'action-btn-wrap' + record.id}>
                <Button
                  title={'Download document from Rabbit Sign'}
                  onClick={() => doRabbitSignAction(RabbitActionTypes.Download, record)}
                  disabled={loading || record.folderStatus !== DocStatus.signed || record.fileId === null}
                  loading={loading}
                  {...btnConfigs}
                  color="primary"
                >
                  <DownloadOutlined />
                </Button>
                <Button
                  title={
                    record.lastReminderDate &&
                    moment(record.lastReminderDate).utc(false).format('MM/dd/yyyy') === moment().utc(false).format('MM/dd/yyyy')
                      ? 'Document sign reminder already has been sent today'
                      : 'Send reminder to sign'
                  }
                  onClick={() => doRabbitSignAction(RabbitActionTypes.SendReminder, record)}
                  disabled={
                    loading ||
                    record.folderStatus === DocStatus.signed ||
                    record.signers?.every((s) => s.status === SignerStatus.signed) ||
                    record.folderStatus === DocStatus.canceled
                  }
                  loading={loading}
                  {...btnConfigs}
                  color="yellow"
                >
                  <ClockCircleOutlined />
                </Button>
                <Button
                  title={'Cancel document from Rabbit Sign'}
                  onClick={() => doRabbitSignAction(RabbitActionTypes.CancelDocument, record)}
                  disabled={
                    loading ||
                    record.folderStatus === DocStatus.signed ||
                    record.folderStatus === DocStatus.canceled ||
                    record.signers?.every((s) => s.status === SignerStatus.signed)
                  }
                  loading={loading}
                  {...btnConfigs}
                  color="danger"
                >
                  <DeleteOutlined />
                </Button>
                <Button
                  title={'Refresh document information from Rabbit Sign'}
                  onClick={() => doRabbitSignAction(RabbitActionTypes.RefreshStatus, record)}
                  disabled={
                    loading || (record.folderStatus === DocStatus.signed && record.fileId !== null) || record.folderStatus === DocStatus.canceled
                  }
                  loading={loading}
                  {...btnConfigs}
                  color="green"
                >
                  <RedoOutlined />
                </Button>
              </Space>
            )}
          />
          <Table.Column className="rabbit-sign-doc-cell" title="Title" dataIndex="title" key="title" />
          <Table.Column className="rabbit-sign-doc-cell" title="Summary" dataIndex="summary" key="summary" />
          <Table.Column
            className="rabbit-sign-doc-cell"
            title="Status"
            dataIndex="folderStatus"
            key="folderStatus"
            render={(signers: RabbitSignSigner[], record: RabbitSignDocument) => (
              <div className="rabbit-sign-doc-status" key={'status-' + record.id}>
                {getFolderStatusTag(record.folderStatus)}{' '}
                <Popover
                  key={'info-record-' + record.id}
                  content={
                    <div className="rabbit-sign-document-info-popover">
                      <Typography.Text strong>
                        <p>Last Updated Date: {moment(record.changeDate).utc(false).format('MM/DD/YYYY h:mm:ss A')}</p>
                        <p>Folder Id: {record.rabbitSignFolderId}</p>
                        <p>Account Used: {RabbitSignApiKeyType[record.apiKeyType]}</p>
                      </Typography.Text>
                    </div>
                  }
                  trigger="hover"
                  placement="right"
                >
                  <ExclamationCircleOutlined className="rabbit-sign-document-info-popover-icon" />
                </Popover>
              </div>
            )}
          />
          <Table.Column
            className="rabbit-sign-doc-cell"
            title="Signers"
            dataIndex="signers"
            key="signers"
            render={(signers: RabbitSignSigner[], record: RabbitSignDocument) => {
              return signers?.length ? (
                <div className="rabbit-sign-signer-list" key={'signers-' + record.id}>
                  {signers.map((signer: RabbitSignSigner) => (
                    <p className="rabbit-sign-signer" key={signer.email + '-' + record.id}>
                      <Popover
                        content={
                          <div className="rabbit-sign-document-signers-popover">
                            <p>Email: {signer.email}</p>
                            <p>Name: {signer.name}</p>
                            <p>Status: {getSignerStatusTag(signer.status)}</p>
                            <p>Signing Order: {signer.signingOrder}</p>
                          </div>
                        }
                      >
                        <span>
                          {signer.email + ' '}
                          {getSignerStatusTag(signer.status)}
                        </span>
                      </Popover>
                    </p>
                  ))}
                </div>
              ) : null;
            }}
          />
          <Table.Column
            className="rabbit-sign-doc-cell"
            title="CC"
            dataIndex="ccList"
            key="ccList"
            render={(cc: string[]) => {
              return cc?.length ? <span>{cc.join(', ')}</span> : null;
            }}
          />
          <Table.Column
            className="rabbit-sign-doc-cell"
            title="Created"
            dataIndex="createDate"
            key="createDate"
            render={(date) => <>{moment(date).utc(false).format('MM/DD/YYYY h:mm:ss A')}</>}
          />
          <Table.Column
            className="rabbit-sign-doc-cell"
            title="Updated"
            dataIndex="changeDate"
            key="changeDate"
            render={(date) => <>{moment(date).utc(false).format('MM/DD/YYYY h:mm:ss A')}</>}
          />
        </Table>
      </Card>
    </div>
  );
};

export default withErrorHandler(ESignDocumentsComponent, axios);
